// @ts-nocheck TODO migrate

import type { Payout } from '@packages/domain/models/payout';
import { cleanEmptyKeys } from '@packages/domain/utils/cleanEmptyKeys/cleanEmptyKeys';
import { exchangeLoginToken } from 'actions/authentication/authentication';
import { fetchOrder } from 'actions/entities/orders/fetchOrder';
import { setPayouts } from 'actions/entities/payouts/payouts';
import { fetchSender } from 'actions/entities/sender/sender';
import {
  fetchEmbedded,
  hideUserManagement,
  setEmbedded,
  setEmbeddedConfirmationStep,
  setFieldsValues,
  setFieldValue,
  setGatewayOrigin,
  setMaxAmount,
  setPrefilledFields,
  setReadOnlyFields,
  setRecipient,
  setTheme,
} from 'actions/ui/ui';
import { Chat } from 'components/Chat/Chat';
import type { FieldValue } from 'models/field';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { EmbeddedRepository as embedded } from 'repositories/embedded/embedded';
import { logger } from 'services/logger/logger';

import { EmbeddedStyle } from './EmbeddedStyle/EmbeddedStyle';

const SENDER_COUNTRY_FIELD = 'sender_country';
const toArray = object => Object.values(object);

interface Props {
  exchangeLoginToken: (token: string) => void;
  fetchEmbedded: (state: boolean) => void;
  fetchOrder: (options: { orderId?: string; token: string }) => void;
  fetchSender: () => void;
  hideUserManagement: () => void;
  setEmbedded: () => void;
  setEmbeddedConfirmationStep: () => void;
  setFieldsValues: (fields: Record<string, FieldValue>) => void;
  setFieldValue: (field: string, value: string) => void;
  setGatewayOrigin: () => void;
  setMaxAmount: (amount: number) => void;
  setPayouts: (payouts: Payout[]) => void;
  setPrefilledFields: (fields: string[]) => void;
  setReadOnlyFields: (fields: string[]) => void;
  setRecipient: (destination: string) => void;
  setTheme: (theme: string) => void;
}

function EmbeddedSetupComponent({
  exchangeLoginToken,
  fetchEmbedded,
  fetchOrder,
  fetchSender,
  hideUserManagement,
  setEmbedded,
  setEmbeddedConfirmationStep,
  setFieldValue,
  setFieldsValues,
  setGatewayOrigin,
  setMaxAmount,
  setPayouts,
  setPrefilledFields,
  setReadOnlyFields,
  setRecipient,
  setTheme,
}: Props) {
  const isMounted = useRef(false);
  const [isFetching, setIsFetching] = useState(true);

  function logEmbeddedProps() {
    logger.log({
      description: 'embedded parameters',
      parameters: embedded.getProps(),
    });
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  useEffect(() => {
    const initialize = async () => {
      logEmbeddedProps();
      const {
        country,
        destination,
        env: _env,
        hideUserManagement: shouldHideUserManagement,
        locale: _locale,
        onPaymentCreated: _onPaymentCreated,
        loginToken,
        orderId,
        orderToken,
        payables: payouts,
        read_only: readOnly = '',
        max_amount: maxAmount,
        theme,
        token,
        showConfirmationStep,
        ...embeddedFields
      } = embedded.getProps();

      fetchEmbedded(true);
      setEmbedded();
      setGatewayOrigin();
      if (maxAmount) {
        setMaxAmount(maxAmount);
      }
      if (shouldHideUserManagement === true) {
        hideUserManagement();
      }
      setTheme(theme);
      if (loginToken) {
        exchangeLoginToken(loginToken);
      }

      const embeddedFieldsWithValues = cleanEmptyKeys(embeddedFields);
      const prefilledFields = Object.keys(embeddedFieldsWithValues);
      setPrefilledFields(prefilledFields);

      if (token) {
        await fetchOrder({ token });
      } else {
        if (payouts) {
          setPayouts(toArray(payouts));
        }
        setRecipient(destination);
        setReadOnlyFields(readOnly.split(/\s*,\s*/));
        setFieldsValues(embeddedFields);
        if (country) {
          setFieldValue(SENDER_COUNTRY_FIELD, country);
        }
        await fetchSender();
        if (orderId || orderToken) {
          await fetchOrder({ orderId, token: orderToken });
        }
      }

      if (showConfirmationStep) {
        setEmbeddedConfirmationStep(showConfirmationStep);
      }

      setIsFetching(false);
      fetchEmbedded(false);
    };

    if (!isMounted.current) {
      initialize();
    }
    return () => {
      isMounted.current = true;
    };
  }, []);

  if (isFetching) {
    return null;
  }

  return (
    <>
      <Chat />
      <EmbeddedStyle />
    </>
  );
}

const mapDispatchToProps = {
  fetchEmbedded,
  fetchOrder,
  fetchSender,
  exchangeLoginToken,
  hideUserManagement,
  setEmbedded,
  setGatewayOrigin,
  setFieldValue,
  setFieldsValues,
  setReadOnlyFields,
  setRecipient,
  setPayouts,
  setPrefilledFields,
  setTheme,
  setMaxAmount,
  setEmbeddedConfirmationStep,
};

const EmbeddedSetup = connect(null, mapDispatchToProps)(EmbeddedSetupComponent);

export { EmbeddedSetup };
